import { IAmplifyProvider } from "../hexagon/adapters/driven/providers/amplifyProvider";
import { IlocalStorageProvider } from "../hexagon/adapters/driven/providers/localStorageProvider";
import { IAuthController } from "../hexagon/adapters/driver/authController";
import { useAppDispatch, useAppSelector } from "./redux";
import { setAuthState, setUser } from "../redux/Auth/auth.slice";
import {
  setClinicFloorList,
  setSelectedFloors,
  setUserClinic,
  setClinicState,
  setRedirectToUserInfo,
  setAssignedDevice,
} from "../redux/Clinic/clinic.slice";
import { setLayoutState } from "../redux/Layout/layout.slice";
import { setNotificationSettings } from "../redux/Setting/setting.slice";
import { featureFlag } from "../constants/feature";
import type { ITimeProvider } from "../hexagon/adapters/driven/providers/provider";
import { AppNotificationType } from "../constants/appNotification";
import type { IAppNotification } from "../types/app-notification";
import { setClinicalAlertFilter } from "../redux/Alert/alert.slice";
import { setDashboardFloor } from "../redux/Dashboard/dashboard.slice";
import { setSummaryState } from "../redux/Summary/summary.slice";
import type { ISummaryController } from "../hexagon/adapters/driver/summaryController";
import { setHomehealthState } from "../redux/Homehealth/homehealth.slice";
import { IHomeHealthController } from "../hexagon/adapters/driver/homeHealthController";
import { ISMSController } from "../hexagon/adapters/driver/smsController";

// Default escalated alert levels for new users
const initialEscalatedAlertLevels = [
  { value: "EscalatedLevel1" },
  { value: "EscalatedLevel2" },
  { value: "EscalatedLevel3" },
];

export const useRouterHook = ({
  amplifyProvider,
  authController,
  patientController,
  clinicController,
  localStorageProvider,
  notificationController,
  timeProvider,
  appNotification,
  circadiaEngineProvider,
  summaryController,
  homeHealthController,
  isPIN,
  smsController,
}: {
  amplifyProvider: IAmplifyProvider;
  authController: IAuthController;
  clinicController: IClinicController;
  localStorageProvider: IlocalStorageProvider;
  notificationController: INotificationController;
  timeProvider: ITimeProvider;
  appNotification: IAppNotification;
  patientController: IPatientController;
  circadiaEngineProvider: ICircadiaEngineProvider;
  summaryController: ISummaryController;
  homeHealthController: IHomeHealthController;
  smsController: ISMSController;
  isPIN: () => boolean;
}) => {
  let isRedirectToUserInfo = false;
  let storedBiometricValue = null;
  const storedValue = localStorageProvider.get("isBiometric");
  if (storedValue !== null) {
    storedBiometricValue = storedValue === "true";
  }
  const dispatch = useAppDispatch();
  const { dashboardFloor, clinicList: _clinicList } = useAppSelector(
    (state) => ({
      dashboardFloor: state.dashboard.dashboardFloor,
      clinicList: state.clinic?.clinicList || [],
    })
  );
  const populateAppData = async (changeFacility: boolean = false) => {
    amplifyProvider.amplifyConfigure();
    if (changeFacility) {
      localStorageProvider.remove("dashboard_filters");
    }
    dispatch(
      setLayoutState({
        layoutState: "loading",
      })
    );
    const response = await notificationController.getNotificationSetting();
    let getFacilityIdFromNotificationSetting = null;
    if (response.success) {
      getFacilityIdFromNotificationSetting =
        response.data?.data.notification_preference
          .facilities_to_send_notification_for_old_id?.[0];
    }
    const { success: clinicSuccess, data: clinicData } =
      await clinicController.getMyClinics();

    if (clinicSuccess && clinicData) {
      if (clinicData.length === 0) {
        isRedirectToUserInfo = true;
        dispatch(setRedirectToUserInfo(true));
      }
      dispatch(
        setClinicState({
          clinicState: "loading",
          clinicList: clinicData.map((item) => {
            return item;
          }),
        })
      );
      let selectedClinic = null;
      if (
        getFacilityIdFromNotificationSetting &&
        response?.data?.data?.notification_preference
      ) {
        selectedClinic =
          clinicData.find(
            (item) => item.id === Number(getFacilityIdFromNotificationSetting)
          ) || clinicData[0];
        selectedClinic = await clinicController.setSelectedClinic({
          id: selectedClinic?.id || 0,
          clinicList: clinicData,
          notificationSettings: {
            patient_id: response?.data?.data?.patient_id || 0,
            email: response?.data?.data?.notification_preference?.email,
            pushNotification:
              response?.data?.data?.notification_preference?.push_notification,
            kioskMode:
              response?.data?.data?.notification_preference?.kiosk_mode,
            escalatedAlertPreference:
              response?.data?.data?.notification_preference
                ?.escalated_alert_preference,
            facilitiesToSendNotificationForOldId: [selectedClinic?.id || 0],
            psnAlertPreference:
              response?.data?.data?.notification_preference
                ?.psn_alert_preference,
            bedExitPreference:
              response?.data?.data?.notification_preference
                ?.bed_exit_preference,
            sms: response?.data?.data?.notification_preference?.sms,
            smsPreferences:
              response?.data?.data?.notification_preference?.sms_preferences,
          },
        });
      }
      if (!selectedClinic) {
        selectedClinic = clinicController.setDefaultClinic({
          clinicList: clinicData,
        });
      }

      const { data: smsData } = await smsController.getPhoneNumber();
      dispatch(
        setAuthState({
          smsData: {
            phoneNumber: smsData?.phoneNumber || "",
            isVerified: smsData?.isVerified || false,
          },
        })
      );

      const selectedFloors: number[] = [];
      // Fetch assigned device to a patient
      const { data: fetchAssignedDeviceData } =
        await clinicController.fetchAssignedDevice({
          clinic_id: selectedClinic?.id || 0,
          patient_id: response?.data?.data?.patient_id || 0,
        });
      // Store assigned device in Redux (null if no device assigned)
      dispatch(setAssignedDevice(fetchAssignedDeviceData || null));
      dispatch(setUser({ patient_id: response?.data?.data?.patient_id || 0 }));
      const { data } = await authController.getCurrentUser();
      if (
        !isRedirectToUserInfo &&
        !data?.user?.fullName &&
        !localStorageProvider.get("userInfoUpdated")
      ) {
        isRedirectToUserInfo = true;
        dispatch(setRedirectToUserInfo(true));
      }
      let patientId = null;
      if (selectedClinic?.is_home_health_enabled && !data?.user?.homeHealthId) {
        const res = await homeHealthController.getSelectedFacilityPatientList({
          clinic_id: selectedClinic.id,
        });

        const { success: homeHealthSuccess, data: homeHealthData } = res;
        if (homeHealthSuccess && homeHealthData) {
          const c100DeviceIdDictionary = homeHealthData.reduce(
            (acc: Dictionary<IHHPatient>, item) => {
              // get c100_device_id from item.patient_id
              const c100DeviceId = item.devices.find(
                (device) => device.device_type === "c100"
              );
              if (c100DeviceId) {
                acc[c100DeviceId.device_id] = item;
              }
              return acc;
            },
            {}
          );

          dispatch(
            setHomehealthState({
              patients: homeHealthData,
              c100DeviceIdDictionary,
            })
          );
        }
      } else if (
        selectedClinic?.is_home_health_enabled &&
        data?.user?.homeHealthId
      ) {
        const res = await homeHealthController.getSinglePatientInfo({
          patient_id: data?.user?.homeHealthId,
        });
        if (res.success && res.data) {
          const c100DeviceIdDictionary: Dictionary<IHHPatient> = {};
          res.data.devices.forEach((item) => {
            if (item.device_type === "c100") {
              item._id = res.data?._id;
              c100DeviceIdDictionary[item.device_id] = item;
              patientId = item.device_id;
            }
          });
          dispatch(
            setHomehealthState({
              patients: [{ ...res.data, patient_id: patientId }],
              c100DeviceIdDictionary,
            })
          );
          window.location.replace(`/#/patient-profile/${patientId}`);
        }
      }

      if (selectedClinic) {
        timeProvider.setTimezone(selectedClinic.timezone || "");
        dispatch(setUserClinic({ clinic: selectedClinic }));

        const { success: floorSuccess, data: floorData } =
          await clinicController.getClinicFloor({
            clinicId: selectedClinic.id,
          });

        if (floorSuccess && floorData) {
          dispatch(setClinicFloorList({ clinicFloorList: floorData }));
          floorData.forEach((item) => {
            if (item.enabled) selectedFloors.push(item.floor_id);
          });
          let selectFloors = selectedFloors;
          // If any floor is not selected by default select all
          if (floorData.length > 0 && selectedFloors.length === 0) {
            const Allfloors = await clinicController.setClinicFloor({
              clinicId: selectedClinic.id,
              floor_ids: floorData.map((item) => item.floor_id),
            });
            selectFloors = Allfloors.data?.floors || [];
            dispatch(
              setClinicFloorList({
                clinicFloorList: floorData.map((item) => ({
                  ...item,
                  enabled: true,
                })),
              })
            );
          }
          dispatch(setSelectedFloors(selectFloors));
          if (changeFacility || selectFloors.length >= dashboardFloor.length) {
            dispatch(setDashboardFloor(selectFloors));
            dispatch(
              setClinicalAlertFilter({
                floors: selectFloors.map((item) => Number(item)),
              })
            );
          }
        }

        // Notification Settings
        if (featureFlag.notificationSettings) {
          const isNewUser = localStorageProvider.get("isNewUser");
          if (response.data) {
            let notificationRes = null;
            if (isNewUser) {
              const defaultConfig = {
                email: false,
                pushNotification: true,
                kioskMode: false,
                escalatedAlertPreference: initialEscalatedAlertLevels.map(
                  (item) => item.value
                ),
                facilitiesToSendNotificationForOldId: [selectedClinic.id],
                psnAlertPreference: ["PsnLevel3Acute", "PsnLevel3"],
                bedExitPreference: [],
                // patient_id must be included so ConnectDevice can assign a
                // device on the very first session without closing and re-opening.
                patient_id: response.data.data?.patient_id || 0,
              };
              notificationRes =
                await notificationController.setNotificationSetting(
                  defaultConfig
                );
              dispatch(setNotificationSettings(defaultConfig));
              localStorageProvider.remove("isNewUser");
            } else {
              notificationRes =
                await notificationController.setNotificationSetting({
                  email: response.data.data?.notification_preference?.email,
                  pushNotification:
                    response.data.data?.notification_preference
                      ?.push_notification,
                  kioskMode:
                    response.data.data?.notification_preference?.kiosk_mode,
                  escalatedAlertPreference:
                    response.data.data?.notification_preference
                      ?.escalated_alert_preference,
                  facilitiesToSendNotificationForOldId: [selectedClinic.id],
                  psnAlertPreference:
                    response.data.data?.notification_preference
                      ?.psn_alert_preference,
                  bedExitPreference:
                    response.data.data?.notification_preference
                      ?.bed_exit_preference,
                  sms: response.data.data?.notification_preference?.sms,
                  smsPreferences:
                    response.data.data?.notification_preference
                      ?.sms_preferences,
                });

              dispatch(
                setNotificationSettings({
                  email: response.data.data?.notification_preference?.email,
                  patient_id: response.data.data?.patient_id || 0,
                  pushNotification:
                    response.data.data?.notification_preference
                      ?.push_notification,
                  kioskMode:
                    response.data.data?.notification_preference?.kiosk_mode,
                  escalatedAlertPreference:
                    response.data.data?.notification_preference
                      ?.escalated_alert_preference,
                  facilitiesToSendNotificationForOldId: [
                    +response.data.data?.notification_preference
                      ?.facilities_to_send_notification_for_old_id,
                  ],
                  psnAlertPreference:
                    response.data.data?.notification_preference
                      ?.psn_alert_preference,
                  bedExitPreference:
                    response.data.data?.notification_preference
                      ?.bed_exit_preference,
                  sms: response.data.data?.notification_preference?.sms,
                  smsPreferences:
                    response.data.data?.notification_preference
                      ?.sms_preferences,
                })
              );
            }

            if (notificationRes.error) {
              appNotification({
                nTitle: notificationRes.error.message,
                nType: AppNotificationType.ERROR,
              });
              return;
            }

            if (notificationRes.data) {
              dispatch(
                setAuthState({
                  userPreference: notificationRes.data.data,
                })
              );
            }
          }
        }
        //

        // Summary
        const { success: summarySuccess, data: summaryData } =
          await summaryController.getSummary({
            clinicId: selectedClinic.id,
            floorIds: selectedFloors,
            timezone: selectedClinic.timezone || "",
          });

        if (summarySuccess && summaryData) {
          dispatch(setSummaryState({ summary: summaryData }));
        }
        //
      }
      dispatch(setClinicState({ clinicState: "pending" }));
    }

    const { data } = await authController.getCurrentUser();
    const { data: hmacData } = await patientController.getPatientHmac({
      platform: circadiaEngineProvider.plat,
    });
    const user = data?.user;
    const hmac = hmacData?.data;

    if (user && hmac && clinicData?.length) {
      let selectedClinic = clinicController.getSelectedClinic({
        clinicList: clinicData,
      });
      if (selectedClinic && selectedClinic?.name && user.fullName) {
        const token = await authController.getAuthenticationToken();
        circadiaEngineProvider.loginIntercomUser(token, hmac, {
          name: user.fullName,
          user_id: user.userID,
          email: user.email,
          facility: selectedClinic?.name,
        });
      } else if (user.userID) {
        const token = await authController.getAuthenticationToken();
        circadiaEngineProvider.loginIntercomUser(token, hmac, {
          name: user.fullName || "",
          user_id: user.userID,
          email: user.email || "",
          facility: selectedClinic?.name || "",
        });
      }
    }

    // const { data: userBetaFeaturesData } =
    //   await userController.getUserBetaFeatures();

    // end
    dispatch(
      setLayoutState({
        layoutState: "pending",
      })
    );
  };

  const getAuthState = async () => {
    dispatch(
      setAuthState({
        authState: "loading",
      })
    );

    // Z-Pax Auth Integration will be handled by useZpaxAuth hook in components
    // The hook handles detection, callback parsing, and token exchange
    // Normal auth flow continues here

    let result = await authController.getCurrentUser();

    // Retry once on transient failure — a network blip during token refresh
    // shouldn't permanently log the user out.
    if (
      !result.data?.user &&
      result.error !== "pinRequired" &&
      typeof result.error === "string" &&
      !["UNAUTHORIZED", "NO_TOKEN"].some((code) =>
        (result.error as string).toUpperCase().includes(code)
      )
    ) {
      await new Promise((r) => setTimeout(r, 2000));
      result = await authController.getCurrentUser();
    }

    const { data, error } = result;
    if (error && error === "pinRequired") {
      dispatch(
        setAuthState({
          isAuthorized: true,
          authState: "pending",
        })
      );
      return;
    }

    if (!data || !data.user) {
      dispatch(
        setAuthState({
          authState: "pending",
          isAuthorized: false,
          isPinAuthorized: false,
        })
      );
      return;
    }
    const isCredentials = localStorageProvider.get("internalUse");
    const isBiometric = localStorageProvider.get("isBiometric");
    if (data && data.user && isPIN() ? isCredentials : true) {
      localStorageProvider.set(
        "username",
        `${data.user?.firstName} ${data.user?.lastName || ""}`
      );

      const hasNoAccess = (data.user.groups || []).length < 1;
      if (isBiometric && isBiometric === "true") {
        dispatch(
          setAuthState({
            user: data.user,
            isAuthorized: true,
            isPinAuthorized: true,
            hasNoAccess,
            authState: "pending",
          })
        );
        return "biometric";
      } else {
        dispatch(
          setAuthState({
            user: data.user,
            isAuthorized: true,
            hasNoAccess,
            authState: "pending",
          })
        );
      }
      return;
    }

    dispatch(
      setAuthState({
        authState: "pending",
      })
    );
  };

  return {
    populateAppData,
    getAuthState,
    storedBiometricValue,
  };
};
